import type { PlayerId } from '../../shared/types/ids';
import type { SkillEstimate, SkillEvidence } from '../../shared/types/skills';
import type { SkillRepository } from '../interfaces/repositories';
import { summariseSkillEvidence } from '../../platform/skills/evidence';
import { projectSkillEvidence } from './governed-records';
import type { JsonStore } from './json-store';
import { STORAGE_KEYS } from './keys';
import { toSkillEstimateDto, toSkillEvidenceDto } from './persistence-dto';

/** Bounded per player: estimates are recomputed from evidence, so old rows add little. */
const MAX_EVIDENCE_PER_PLAYER = 500;

type EvidenceTable = Record<PlayerId, SkillEvidence[]>;

/**
 * Skill evidence is the closest thing Thrive keeps to a record of a child, so the DTO
 * projection matters most here: an evidence row carries an outcome and a detector confidence
 * number, never the observation that produced it (architecture §15, §19).
 */
export function createLocalSkillRepository(store: JsonStore): SkillRepository {
  const readEvidence = (): EvidenceTable =>
    store.read(STORAGE_KEYS.skillEvidence, projectSkillEvidence) ?? {};

  return {
    async recordEvidence(evidence: SkillEvidence[]): Promise<void> {
      if (evidence.length === 0) return;
      const table = readEvidence();

      for (const item of evidence) {
        const projected = toSkillEvidenceDto(item);
        if (projected === null) continue;

        const existing = table[projected.playerId] ?? [];
        existing.push(projected);
        table[projected.playerId] =
          existing.length > MAX_EVIDENCE_PER_PLAYER
            ? existing.slice(existing.length - MAX_EVIDENCE_PER_PLAYER)
            : existing;
      }

      store.write(STORAGE_KEYS.skillEvidence, table);
    },

    async getSkillEstimates(playerId: PlayerId): Promise<SkillEstimate[]> {
      // Estimates are derived, never stored as a second source of truth that could drift
      // away from the evidence behind it.
      const estimates = summariseSkillEvidence(readEvidence()[playerId] ?? []);
      return estimates
        .map(toSkillEstimateDto)
        .filter((estimate): estimate is SkillEstimate => estimate !== null);
    },

    async getEvidence(playerId: PlayerId): Promise<SkillEvidence[]> {
      return readEvidence()[playerId] ?? [];
    },
  };
}
